import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { AdminActions } from "src/redux/Admin/actions";
import { formatKst } from "src/utils/formatKst";
import styles from "./styles.module.scss";

// Auth-action codes -> readable labels (no internal codes in the UI).
const ACTION_LABELS = {
    SIGNED_UP: "Signed up",
    EMAIL_VERIFIED: "Verified email",
    SIGN_IN_SUCCESS: "Signed in",
    SIGN_IN_FAILED: "Sign-in failed",
    SIGN_IN_FAILED_PASSWORD: "Sign-in failed (password)",
    SIGN_IN_FAILED_TOTP: "Sign-in failed (TOTP)",
    SIGN_OUT: "Signed out",
};

const actionLabel = (code) => ACTION_LABELS[code] || code;

// Keycloak error codes -> readable text (no raw codes in the UI).
const DETAIL_LABELS = {
    invalid_user_credentials: "Invalid user credentials",
    user_not_found: "User not found",
    account_disabled: "Account disabled",
    user_disabled: "User disabled",
    user_temporarily_disabled: "User temporarily disabled",
    invalid_token: "Invalid token",
    expired_code: "Expired code",
};

const detailLabel = (d) => {
    if (!d) return "-";
    if (DETAIL_LABELS[d]) return DETAIL_LABELS[d];
    // Humanize any other snake_case code; leave free text (e.g. "MFA (TOTP)") as is.
    return /^[a-z0-9_]+$/.test(d) ? d.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : d;
};

// A short, readable device from the user-agent (browser + OS), full text on hover.
const device = (ua) => {
    if (!ua) return "-";
    const browser = /Edg\//.test(ua) ? "Edge"
        : /Chrome\//.test(ua) ? "Chrome"
        : /Firefox\//.test(ua) ? "Firefox"
        : /Safari\//.test(ua) ? "Safari"
        : "Browser";
    const os = /Windows/.test(ua) ? "Windows"
        : /Mac OS X|Macintosh/.test(ua) ? "macOS"
        : /Android/.test(ua) ? "Android"
        : /iPhone|iPad/.test(ua) ? "iOS"
        : /Linux/.test(ua) ? "Linux"
        : "";
    return os ? `${browser} / ${os}` : browser;
};

/**
 * A user's auth-action history (route /users/:cptyCode/:userId/history), opened
 * from the History button. Shows signed-up / verified-email / sign-in /
 * sign-out events recorded by auth-gateway.
 */
const UserHistory = () => {
    const { cptyCode, userId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const user = location.state && location.state.user;
    const who = user ? user.email : userId;
    const realm = (user && user.realm) || (cptyCode === "platform" ? "ksl-admin" : "truweb");

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let live = true;
        setLoading(true);
        setError(null);
        dispatch(AdminActions.FetchUserActivity(realm, userId))
            .then((rows) => { if (live) setEvents(rows || []); })
            .catch((e) => { if (live) setError(e.message); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realm, userId]);

    return (
        <div className={styles.userManagement}>
            <div className={`${styles.header} ${styles.headerBack}`}>
                <button
                    className={styles.detailBtn}
                    data-testid="user-history-back"
                    onClick={() => history.push(cptyCode === "platform" ? "/users" : `/users/${cptyCode}`)}
                >
                    Back to users
                </button>
                <div className={styles.title}>Activity: {who}</div>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.section}>
                {loading ? (
                    <div className={styles.loading}>Loading activity…</div>
                ) : (
                    <table className={`${styles.table} ${styles.historyTable}`} data-testid="user-history-table">
                        <thead>
                            <tr>
                                <th>Time</th><th>Action</th><th>IP</th><th>Device</th><th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.length === 0 ? (
                                <tr><td colSpan={5} className={styles.empty}>No activity recorded for this user yet.</td></tr>
                            ) : events.map((e, i) => (
                                <tr key={i} data-testid="user-history-row">
                                    <td>{formatKst(e.tsMs)}</td>
                                    <td>{actionLabel(e.eventType)}</td>
                                    <td>{e.ip || "-"}</td>
                                    <td title={e.userAgent || ""}>{device(e.userAgent)}</td>
                                    <td>{detailLabel(e.details)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default UserHistory;
